import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "../features.css";
import API from "../../utils/API";
import { FEATURES } from "../../config/features";
import { useBookData, resolveAgentName } from "../../data/BookDataProvider";
import { useAuth } from "../../auth/AuthProvider";
import { daysUntil, formatDate } from "../../data/format";
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  Select,
  Tabs,
  useToast,
  ServiceIcon
} from "../../ui";
import { srTypeLabel, URGENCY_LABELS } from "./srLabels";

/**
 * Staff service queue (/app/service) — W6. Open / Resolved tabs,
 * age-forward ordering (oldest open first — nothing gets dropped), client
 * linked to the hub, resolve inline via the EXISTING deployed update
 * endpoint. With FEATURES.srLifecycle (H Tranche 3, shipped on their
 * branch): Claim + a status Select per the shipped PATCH routes; `closed`
 * syncs the legacy completed flag server-side.
 */

const SR_STATUSES = [
  { value: "new", label: "Open" },
  { value: "in_progress", label: "In progress" },
  { value: "waiting_on_client", label: "Waiting on client" },
  { value: "closed", label: "Closed" }
];

function ServiceQueuePage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { agents, identity } = useAuth();
  const { loading, requests, households, refresh } = useBookData();
  const [tab, setTab] = useState("open");
  const [resolving, setResolving] = useState(null);

  // Digest deep-link (?sr=<id>): open the SR's tab (Resolved if it's already
  // closed), then scroll to and pulse its row — the ?prospect= pattern from the
  // pipeline. Inert until the digest actually links here (worker T6).
  const [searchParams] = useSearchParams();
  const targetSr = searchParams.get("sr");
  useEffect(() => {
    if (!targetSr || loading) {
      return undefined;
    }
    const match = requests.find((r) => String(r._id) === String(targetSr));
    if (match && match.completed) {
      setTab("resolved");
    }
    const t = window.setTimeout(() => {
      const el = document.querySelector(".feat-target-pulse");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
    return () => window.clearTimeout(t);
  }, [targetSr, loading, requests]);

  const claim = async (request) => {
    try {
      await API.claimServiceRequest(request._id);
      toast.success("Claimed.");
      refresh();
    } catch (err) {
      toast.error("Couldn't claim the request — try again.");
    }
  };

  const setStatus = async (request, status) => {
    try {
      await API.setServiceRequestStatus(request._id, { status });
      toast.success("Status updated.");
      refresh();
    } catch (err) {
      toast.error("Couldn't update the status — try again.");
    }
  };

  const byClient = useMemo(() => new Map(households.map((h) => [h.id, h])), [households]);

  const rows = useMemo(() => {
    const list = requests.filter((r) => (tab === "open" ? !r.completed : r.completed));
    return [...list].sort((a, b) =>
      tab === "open"
        ? new Date(a.created_at || 0) - new Date(b.created_at || 0) // oldest first
        : new Date(b.created_at || 0) - new Date(a.created_at || 0)
    );
  }, [requests, tab]);

  const resolve = async (request) => {
    setResolving(request._id);
    try {
      await API.updateServiceRequest(request._id, { completed: true });
      toast.success("Marked resolved.");
      refresh();
    } catch (err) {
      toast.error("Couldn't resolve the request — try again.");
    } finally {
      setResolving(null);
    }
  };

  const columns = [
    {
      key: "client",
      header: "Client",
      sortValue: (r) => {
        const h = byClient.get(String((r.client && r.client._id) || r.client));
        return (h ? h.name : r.name || "").toLowerCase();
      },
      render: (r) => {
        const h = byClient.get(String((r.client && r.client._id) || r.client));
        return (
          <span className="feat-cell-stack">
            <span className="feat-cell-primary">{h ? h.name : r.name || "Unknown client"}</span>
            {r.requesterID || r.requester ? (
              <span className="feat-cell-secondary">
                filed by {resolveAgentName(agents, r.requester || r.requesterID) || "an agent"}
              </span>
            ) : null}
          </span>
        );
      }
    },
    {
      key: "type",
      header: "Type",
      render: (r) => (
        <span className="feat-cell-stack">
          <span className="feat-badges">
            <Badge variant="neutral">{srTypeLabel(r.type)}</Badge>
            {r.urgency && r.urgency !== "normal" ? (
              <Badge variant={r.urgency === "high" ? "warning" : "neutral"}>
                {URGENCY_LABELS[r.urgency] || r.urgency}
              </Badge>
            ) : null}
          </span>
          {/* Coerced legacy free-text stays visible so nothing is lost. */}
          {r.type === "other" && r.rawType ? (
            <span className="feat-cell-secondary">was: {r.rawType}</span>
          ) : null}
        </span>
      )
    },
    {
      key: "message",
      header: "Request",
      sortable: false,
      render: (r) => (
        <span className="feat-cell-secondary" style={{ whiteSpace: "normal" }}>
          {r.message}
        </span>
      )
    },
    {
      key: "age",
      header: "Age",
      align: "num",
      sortValue: (r) => (r.created_at ? -new Date(r.created_at).getTime() : 0),
      render: (r) => {
        const days = r.created_at ? -daysUntil(r.created_at) : null;
        if (days == null) {
          return "—";
        }
        const variant = tab === "open" && days > 7 ? "warning" : "neutral";
        return <Badge variant={variant}>{days <= 0 ? "today" : `${days}d`}</Badge>;
      }
    },
    {
      key: "actions",
      header: "",
      sortable: false,
      render: (r) => {
        if (tab !== "open") {
          return <span className="feat-cell-secondary">{formatDate(r.created_at)}</span>;
        }
        if (FEATURES.srLifecycle) {
          const assignedToMe =
            identity && r.assigneeRef && String(r.assigneeRef) === String(identity._id);
          return (
            <span
              style={{ display: "inline-flex", gap: "var(--space-2)", alignItems: "center" }}
              onClick={(e) => e.stopPropagation()}
            >
              {!r.assigneeRef ? (
                <Button size="sm" variant="secondary" onClick={() => claim(r)}>
                  Claim
                </Button>
              ) : (
                <span className="feat-cell-secondary">
                  {assignedToMe ? "yours" : resolveAgentName(agents, r.assigneeRef) || "assigned"}
                </span>
              )}
              <Select
                className="feat-filter"
                aria-label={`Status for ${r.name}`}
                value={r.status || "new"}
                onChange={(e) => setStatus(r, e.target.value)}
              >
                {SR_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </Select>
            </span>
          );
        }
        return (
          <Button
            size="sm"
            variant="secondary"
            loading={resolving === r._id}
            onClick={(e) => {
              e.stopPropagation();
              resolve(r);
            }}
          >
            Mark resolved
          </Button>
        );
      }
    }
  ];

  const openCount = requests.filter((r) => !r.completed).length;
  const resolvedCount = requests.length - openCount;

  return (
    <div className="shell-page">
      <div className="shell-page__titlerow">
        <h1 className="shell-page__title">Service</h1>
      </div>

      <Tabs
        tabs={[
          { key: "open", label: "Open", count: openCount },
          { key: "resolved", label: "Resolved", count: resolvedCount }
        ]}
        active={tab}
        onChange={setTab}
      />

      <div className="feat-hub-tabpanel">
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r._id}
          rowClassName={(r) =>
            targetSr && String(r._id) === String(targetSr) ? "feat-target-pulse" : ""
          }
          loading={loading}
          countLabel={tab === "open" ? "open requests" : "resolved requests"}
          onRowClick={(r) => {
            const h = byClient.get(String((r.client && r.client._id) || r.client));
            if (h) {
              navigate(`/app/clients/${h.id}?tab=service`);
            }
          }}
          emptyState={
            <EmptyState
              icon={<ServiceIcon size={32} />}
              title={tab === "open" ? "No open requests 🎉" : "No resolved requests yet"}
              message={
                tab === "open"
                  ? "Every service request has been handled."
                  : "Resolved requests keep their history here."
              }
            />
          }
        />
      </div>
    </div>
  );
}

export default ServiceQueuePage;
